import { apiDelete, apiGet, apiPost, apiPut } from "./api";
import { normalizePdrProfile, type PdrSchemaProfile } from "./pdrFields";
import {
  loadEvalHistory,
  type EvalCluster,
  type EvalClusterDocument,
  type EvalResultSnapshot,
  type SavedEvalRun,
  syncEvalHistoryCache,
} from "./evalPlaygroundStorage";

type ApiRunRow = SavedEvalRun & {
  result_count?: number;
  pdr_context_chars?: number;
};

function mapApiRun(row: ApiRunRow): SavedEvalRun {
  return {
    id: row.id,
    saved_at: row.saved_at,
    evidence_id: row.evidence_id,
    project_id: row.project_id || "",
    include_reasoning: row.include_reasoning,
    models: row.models || [],
    pdr_context: row.pdr_context,
    pdr_schema_profile: row.pdr_schema_profile,
    evidence_preview: row.evidence_preview,
    project_preview: row.project_preview,
    results: row.results || [],
  };
}

export type SandboxPdrByProfile = Record<PdrSchemaProfile, string>;

export async function fetchEvalSandboxPdrs(): Promise<SandboxPdrByProfile> {
  try {
    const data = await apiGet<{
      pdr_by_profile?: Partial<SandboxPdrByProfile>;
      pdr_context?: string;
      profile?: string;
    }>("/api/evaluate-synthesis/sandbox-pdr");
    const by = data.pdr_by_profile;
    if (by && (by.slim !== undefined || by.complete !== undefined)) {
      return {
        slim: by.slim || "",
        complete: by.complete || "",
      };
    }
    const prof = normalizePdrProfile(data.profile);
    return {
      slim: prof === "slim" ? data.pdr_context || "" : "",
      complete: prof === "complete" ? data.pdr_context || "" : "",
    };
  } catch {
    return { slim: "", complete: "" };
  }
}

export async function persistEvalSandboxPdr(
  profile: PdrSchemaProfile,
  pdrContext: string,
): Promise<SandboxPdrByProfile> {
  try {
    const data = await apiPut<{
      pdr_by_profile?: Partial<SandboxPdrByProfile>;
    }>("/api/evaluate-synthesis/sandbox-pdr", {
      profile,
      pdr_context: pdrContext,
    });
    const by = data.pdr_by_profile;
    if (by) {
      return {
        slim: by.slim || "",
        complete: by.complete || "",
      };
    }
  } catch {
    /* offline */
  }
  return fetchEvalSandboxPdrs();
}

export async function fetchEvalRuns(limit = 50): Promise<SavedEvalRun[]> {
  try {
    const data = await apiGet<{ runs: ApiRunRow[] }>(
      `/api/evaluate-synthesis/runs?limit=${limit}`,
    );
    const runs = (data.runs || []).map(mapApiRun);
    if (runs.length) syncEvalHistoryCache(runs);
    return runs;
  } catch {
    return loadEvalHistory();
  }
}

export async function fetchEvalRunDetail(runId: string): Promise<SavedEvalRun> {
  const data = await apiGet<{ run: ApiRunRow }>(
    `/api/evaluate-synthesis/runs/${encodeURIComponent(runId)}`,
  );
  const run = mapApiRun(data.run);
  syncEvalHistoryCache([run]);
  return run;
}

export async function fetchEvalClusters(): Promise<EvalCluster[]> {
  const data = await apiGet<{ clusters?: EvalCluster[] }>("/api/evaluate-synthesis/clusters");
  return data.clusters || [];
}

export async function fetchEvalClusterDocuments(
  bucketId: string,
): Promise<{ cluster: EvalCluster; documents: EvalClusterDocument[] }> {
  const q = new URLSearchParams({ bucket_id: bucketId });
  const data = await apiGet<{ cluster: EvalCluster; documents?: EvalClusterDocument[] }>(
    `/api/evaluate-synthesis/clusters/documents?${q}`,
  );
  return { cluster: data.cluster, documents: data.documents || [] };
}

/** Start an evaluation as a background job; the server answers before any model is called. */
export async function startEvalRun(body: Record<string, unknown>): Promise<{ job_id: string; models: string[] }> {
  return apiPost<{ job_id: string; models: string[] }>("/api/evaluate-synthesis", {
    ...body,
    background: true,
  });
}

export type EvalJobStatus<T> = {
  status: string;
  error?: string;
  /** While running: each finished model; once done: `eval`, the full response. */
  result?: { partial?: { models?: string[]; results?: EvalResultSnapshot[] }; eval?: T };
  job_record?: { created_at?: string; payload?: { models?: string[] } };
};

export async function fetchEvalJob<T>(jobId: string): Promise<EvalJobStatus<T>> {
  return apiGet<EvalJobStatus<T>>(`/api/jobs/${encodeURIComponent(jobId)}`, { timeoutMs: 30_000 });
}

export async function deleteEvalRunRemote(runId: string): Promise<void> {
  try {
    await apiDelete(`/api/evaluate-synthesis/runs/${encodeURIComponent(runId)}`);
  } catch {
    /* offline or API unavailable */
  }
}
